import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { get, orderBy } from 'lodash';
import moment from 'moment';
import { useParams } from 'react-router-dom';
import { Text } from 'office-ui-fabric-react/lib/Text';
import { Spinner, SpinnerSize } from 'office-ui-fabric-react/lib/Spinner';
import { DetailsList, DetailsRow, SelectionMode, Stack } from 'office-ui-fabric-react';
import SectionHeader from '../../components/SectionHeader';
import PageTitle from '../../components/PageTitle';
import styles from './AdvanceGroupDetails.style';
import Module from '../../components/Module';
import ModuleContent from '../../components/ModuleContent';
import Main from '../../components/Main';
import { fetchAdvanceGroupDetails, fetchAdvanceLogs } from '../../actions/advanceGroups';
import DataList from '../../components/DataList';
import DataItem from '../../components/DataItem';
import SubModuleHeader from '../../components/SubModuleHeader';
import SubModule from '../../components/SubModule';
import SubModuleContent from '../../components/SubModuleContent';
import FluentGrid from '../../components/FluentGrid';
import useAPI from '../../hooks/useAPI';
import { hasRole } from '../../utils/auth';
import Link from '../../components/Link';
import AdvanceGroupAssignmentDetails from '../../components/AdvanceGroupAssignmentDetails';
import FilterDetailsList from '../../components/FilterDetailsList';
import { exportGroupAssignmentsToExcel } from '../../components/FilterDetailsList/FilterDetailsList';
import { formatNumberWithCommas } from '../../utils';
import AdvanceGroupActivityList from '../../components/AdvanceGroupActivityList';

const AdvanceGroupDetails = () => {
  const { advancegroupId } = useParams();
  const { isLoading, error } = useAPI(
    () => fetchAdvanceGroupDetails(advancegroupId),
    [advancegroupId],
  );
  const advanceGroup = useSelector(state =>
    get(state, `entities.advancegroups.${advancegroupId}`, null),
  );

  const advanceLogResult = useAPI(() => fetchAdvanceLogs(advancegroupId), [advancegroupId]);
  const isLoadingAdvanceLog = advanceLogResult.isLoading;

  const rawAdvanceLogs = useSelector(state =>
    get(state, `entities.advancelogs.${advancegroupId}`, null),
  );

  const advanceLogs = useMemo(() => {
    if (rawAdvanceLogs) {
      return rawAdvanceLogs;
    }
    return {
      details: [],
      totalAdvancePayment: 0,
      totalOtherAdjustment: 0,
      totalRecoupment: 0,
    };
  }, [rawAdvanceLogs]);

  const isPartner = hasRole('partner');

  const errorDetail = useMemo(() => {
    if (!error) {
      return null;
    }

    let title = 'Whoops!';
    let message = 'Something went wrong. Please refresh the page to try again.';

    if (error.status === 404) {
      message = 'This advance group does not exist. Please try a different link.';
      title = 'Not Found';
    }

    return {
      title,
      message,
    };
  }, [error]);

  if (isLoading) {
    return (
      <Spinner
        data-testid="advancegroupDetailsSpinner"
        size={SpinnerSize.medium}
        className={styles.loadingSpinner}
      />
    );
  }

  if (errorDetail) {
    return (
      <>
        <PageTitle>{errorDetail.title}</PageTitle>
        <Main>
          <Text>{errorDetail.message}</Text>
        </Main>
      </>
    );
  }

  const getStatementDerivedBalance = () => {
    let balance = 0;
    if (advanceLogs) {
      balance =
        advanceLogs.totalAdvancePayment +
        advanceLogs.totalOtherAdjustment -
        advanceLogs.totalRecoupment;
    }
    return balance;
  };

  const calculatePendingRecoupments = () => {
    if (advanceLogs) {
      const statementDerivedBalance = getStatementDerivedBalance();
      if (statementDerivedBalance > advanceGroup.calculatedLiveBalance) {
        return statementDerivedBalance - advanceGroup.calculatedLiveBalance;
      }
    }
    return 0;
  };

  const getAdvanceLogItems = () => {
    if (advanceLogs) {
      const list = advanceLogs.details?.reduce((result, item) => {
        // only add recoupment to list if it is non-zero recoupment
        if (!(item.type.text === 'Recoupment' && item.amount === 0)) {
          result.push({
            type: item.type.text === 'Recoupment' ? 'Statement Recoupment' : item.type.text,
            amount: item.amount,
            date: item.date,
          });
        }

        return result;
      }, []);

      const pendingRecoupments = calculatePendingRecoupments();
      if (pendingRecoupments > 0)
        list.push({
          type: 'Pending Recoupment',
          amount: pendingRecoupments,
          date: moment.utc().toString(),
        });

      return orderBy(list, ['date'], ['desc']);
    }
    return [];
  };

  const getAdvanceLogColumns = () => {
    return [
      {
        key: 'type',
        name: 'Type',
        className: styles.column1,
        isResizable: false,
        data: 'string',
        fieldName: 'type',
      },
      {
        key: 'amount',
        name: 'Amount',
        className: styles.column2,
        isResizable: false,
        data: 'string',
        fieldName: 'amount',
        onRender(advanceLog) {
          // if advance balance is negative amount, show in red with parentheses
          if (advanceLog.type === 'Advance Balance' && advanceLog.amount < 0) {
            return (
              <span className={styles.negativeAmount}>
                {`(${formatNumberWithCommas(Math.abs(advanceLog.amount).toFixed(2) || 0.0)})`}
              </span>
            );
          }
          // if recoupment is positive, show in red with parentheses since a positive recoupment is a deduction
          if (
            (advanceLog.type === 'Statement Recoupment' ||
              advanceLog.type === 'Pending Recoupment') &&
            advanceLog.amount > 0
          ) {
            return (
              <span className={styles.negativeAmount}>
                {`(${formatNumberWithCommas(advanceLog.amount?.toFixed(2) || 0.0)})`}
              </span>
            );
          }
          return <span>{`${formatNumberWithCommas(advanceLog.amount?.toFixed(2) || 0.0)}`}</span>;
        },
      },
      {
        key: 'date',
        name: 'Date',
        className: styles.column3,
        isResizable: false,
        data: 'string',
        fieldName: 'date',
        onRender(advanceLog) {
          if (advanceLog.type === 'Statement Recoupment') {
            return (
              <span>
                {moment(advanceLog.date).utc().startOf('month').format('MM/DD/YYYY')} -{' '}
                {moment(advanceLog.date).utc().format('MM/DD/YYYY')}
              </span>
            );
          }
          return <span>{moment(advanceLog.date).utc().format('MM/DD/YYYY')} </span>;
        },
      },
    ];
  };

  const renderHeader = () => (
    <Stack className={styles.headerRow} horizontal horizontalAlign="space-between">
      <Stack.Item className={styles.column1}>
        <Text className={styles.columnHeaderText}> Type </Text>
      </Stack.Item>
      <Stack.Item className={styles.column2}>
        <Text className={styles.columnHeaderText}> Amount </Text>
      </Stack.Item>
      <Stack.Item className={styles.column3}>
        <Text className={styles.columnHeaderText}> Date </Text>
      </Stack.Item>
    </Stack>
  );

  const renderAdvanceGroupBalance = () => {
    const statementDerivedBalance = getStatementDerivedBalance();
    const pendingRecoupments = calculatePendingRecoupments();

    let balance = statementDerivedBalance;

    if (pendingRecoupments > 0) balance = statementDerivedBalance - pendingRecoupments;

    if (balance < 0) {
      return (
        <Text as="p" variant="small" className={styles.negativeAmount}>
          {`(${formatNumberWithCommas(balance.toFixed(2))})`}
        </Text>
      );
    }
    return (
      <Text as="p" variant="small">
        {formatNumberWithCommas(balance.toFixed(2))}
      </Text>
    );
  };

  const renderTotalOtherAdjustment = totalOtherAdjustment => {
    if (totalOtherAdjustment < 0) {
      return (
        <Text as="p" variant="small" className={styles.negativeAmount}>
          {`(${formatNumberWithCommas(Math.abs(totalOtherAdjustment).toFixed(2))})`}
        </Text>
      );
    }

    return (
      <Text as="p" variant="small">
        {`${formatNumberWithCommas(totalOtherAdjustment?.toFixed(2))}`}
      </Text>
    );
  };

  const renderTotalRecoupment = () => {
    const totalStatementRecoupment = advanceLogs?.totalRecoupment || 0.0;
    const pendingRecoupment = calculatePendingRecoupments();
    const totalRecoupment = totalStatementRecoupment + pendingRecoupment;

    if (totalRecoupment > 0) {
      return (
        <Text as="p" variant="small" className={styles.negativeAmount}>
          {`(${formatNumberWithCommas(totalRecoupment?.toFixed(2) || 0.0)})`}
        </Text>
      );
    }

    return (
      <Text as="p" variant="small">
        {`${formatNumberWithCommas(totalRecoupment?.toFixed(2))}`}
      </Text>
    );
  };

  return (
    <>
      {advanceGroup && advanceGroup.contract && (
        <>
          <PageTitle>{advanceGroup.name}</PageTitle>
          <Main>
            <Module>
              <ModuleContent>
                <SubModule disableAccordian>
                  <SubModuleHeader noBorder noSpacing id="'advancegroupDetailsHeading">
                    Advance Group Details
                  </SubModuleHeader>

                  <SubModuleContent>
                    <SectionHeader noBorder noSpacing>
                      Attributes
                    </SectionHeader>
                    <DataList>
                      <DataItem label="Contract" data-testid="advancegroup-contract-name">
                        <Link href={`/contracts/${advanceGroup.contract?.address}`}>
                          {advanceGroup.contract.name}
                        </Link>
                      </DataItem>
                      <DataItem label="Contract ID" data-testid="advancegroup-contract-id">
                        {advanceGroup.contract.contractId}
                      </DataItem>
                      <DataItem
                        label="Contract Currency"
                        data-testid="advancegroup-contract-currency"
                      >
                        {advanceGroup.contract.contractCurrency}
                      </DataItem>
                      <DataItem label="Partner" data-testid="advancegroup-partner-name">
                        <Link href={`/partners/${advanceGroup.contract?.partner?.address}`}>
                          {advanceGroup.contract.partner.name}
                        </Link>
                      </DataItem>
                      <DataItem label="Partner ID" data-testid="advancegroup-partner-id">
                        {advanceGroup.contract.metadata?.vendorId}
                      </DataItem>
                      <DataItem label="Contract Status" data-testid="advancegroup-approval-status">
                        {advanceGroup.contract.approvalStatus === 'Approved'
                          ? 'Active'
                          : advanceGroup.contract.approvalStatus}
                      </DataItem>
                    </DataList>
                    {isLoadingAdvanceLog ? (
                      <Spinner
                        data-testid="advancegroupDetailsSpinner"
                        size={SpinnerSize.medium}
                        className={styles.smallLoadingSpinner}
                      />
                    ) : (
                      <div data-testid="advancegroup-payment-summary-section">
                        <br />
                        <FluentGrid className={styles.primaryBottomBorder}>
                          <FluentGrid row>
                            <FluentGrid col sm={12} md={3} className={styles.keywords}>
                              <SectionHeader noBorder noSpacing>
                                Total Advance Payment
                              </SectionHeader>
                              <Text as="p" variant="small" data-testid="total-advancePayment">
                                {formatNumberWithCommas(
                                  advanceLogs?.totalAdvancePayment?.toFixed(2) || 0.0,
                                )}
                              </Text>
                            </FluentGrid>
                            <FluentGrid col sm={12} md={3} className={styles.keywords}>
                              <SectionHeader noBorder noSpacing>
                                Other Adjustments
                              </SectionHeader>
                              <Text as="p" variant="small" data-testid="total-otherAdjustments">
                                {renderTotalOtherAdjustment(
                                  advanceLogs?.totalOtherAdjustment || 0.0,
                                )}
                              </Text>
                            </FluentGrid>
                            <FluentGrid col sm={12} md={3} className={styles.keywords}>
                              <SectionHeader noBorder noSpacing>
                                Total Recoupment
                              </SectionHeader>
                              <Text
                                as="p"
                                variant="small"
                                className={styles.negativeAmount}
                                data-testid="total-recoupment"
                              >
                                {renderTotalRecoupment()}
                              </Text>
                            </FluentGrid>
                            <FluentGrid col sm={12} md={3} className={styles.keywords}>
                              <SectionHeader noBorder noSpacing>
                                Remaining Balance
                              </SectionHeader>
                              <Text variant="small" data-testid="remaining-balance">
                                {renderAdvanceGroupBalance()}
                              </Text>
                            </FluentGrid>
                          </FluentGrid>
                        </FluentGrid>
                      </div>
                    )}
                  </SubModuleContent>
                </SubModule>

                {!isPartner && (
                  <SubModule disableAccordian data-testid="advancegroup-groupAssignments-section">
                    <SubModuleHeader noBorder>Group Assignment</SubModuleHeader>

                    <SubModuleContent>
                      <AdvanceGroupAssignmentDetails
                        groupAssignments={advanceGroup.groupAssignments}
                      />
                      <FilterDetailsList
                        filters={advanceGroup.groupAssignments.filters}
                        dimensions={advanceGroup.groupAssignments.dimensions}
                        header="Group Assignment Details"
                        buttonText="View Group Assignment Details"
                        onExport={exportGroupAssignmentsToExcel(advanceGroup.groupAssignments)}
                        isGroupAssignmentFilter
                      />
                    </SubModuleContent>
                  </SubModule>
                )}

                <SubModule disableAccordian data-testid="advancegroup-advance-logs-section">
                  <SubModuleHeader noBorder noSpacing id="adjustmentDetailsHeading">
                    Advance Log
                  </SubModuleHeader>
                  <SubModuleContent className={styles.advanceLogSection}>
                    {isLoadingAdvanceLog ? (
                      <Spinner
                        data-testid="advancegroupDetailsSpinner"
                        size={SpinnerSize.medium}
                        className={styles.smallLoadingSpinner}
                      />
                    ) : (
                      <DetailsList
                        items={getAdvanceLogItems()}
                        columns={getAdvanceLogColumns()}
                        selectionMode={SelectionMode.none}
                        onRenderDetailsHeader={renderHeader}
                        enableShimmer={isLoadingAdvanceLog}
                        onRenderRow={props => (
                          <DetailsRow {...props} className={styles.transparentBackground} />
                        )}
                        isHeaderVisible
                      />
                    )}
                  </SubModuleContent>
                </SubModule>
              </ModuleContent>
            </Module>

            <AdvanceGroupActivityList advancegroupId={advancegroupId} />
          </Main>
        </>
      )}
    </>
  );
};

export default AdvanceGroupDetails;
